import { AsyncComponent } from '#/components/async/AsyncComponent';
import { useGet } from '#/hooks';
import { InvoicesKeys } from '#/hooks/api/invoices/InvoicesKeys';
import { useUserHasRoles } from '#/hooks/permissions/useUserHasRoles';
import { useI18n } from '#/hooks/useI18n';
import { API } from '#/lib/network/API';
import { Invoice } from '#/models/invoice/Invoice';
import { ExternalStatusTag } from '#/pages/accounting/invoices/components/ExternalStatusTag';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Divider, Flex, Regular, Semibold } from '@zazume/zzm-base';
import { FC } from 'react';

const Container = styled.div(({ theme }) => css`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.s2} 0 ${theme.spacing.s2} ${theme.spacing.xs};
  border-left: ${theme.borderWeights.bold} solid ${theme.colors.Red};
  word-break: break-all;
`);

interface InvoiceStatusModalInfoProps {
  documentId: string;
}

export const InvoiceStatusModalInfo: FC<InvoiceStatusModalInfoProps> = ({ documentId }) => {
  const { t } = useI18n();
  const apiResult = useGet<Invoice>(InvoicesKeys.fromDocument(documentId), API.invoices.getFromDocumentId(documentId));

  const canSeeExternalStatus = useUserHasRoles('admin', 'buildingManager');

  if (!canSeeExternalStatus) {
    return null;
  }

  return (
    <AsyncComponent apiResult={apiResult}>
      {(invoice) => {
        if (!invoice.externalStatus && !invoice.externalStatusDescription) {
          return null;
        }

        return <div>
          <Divider/>
          <Flex direction="column" gap="s8">
            {invoice.externalStatus && (
              <Flex direction="column" gap="s4">
                <Semibold variant="M">{t('invoices.table.externalStatus' as any)}</Semibold>
                <ExternalStatusTag externalStatus={invoice.externalStatus}/>
              </Flex>
            )}
            {invoice.externalStatusDescription && (
              <Flex direction="column" gap="s4">
                <Semibold variant="M">{t('documentsModals.validationError')}</Semibold>
                <Container>
                  <Regular variant="S" color="Gray500">{invoice.externalStatusDescription}</Regular>
                </Container>
              </Flex>
            )}
          </Flex>
        </div>;
      }}
    </AsyncComponent>
  );
};
